import { useCallback, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Info, Pencil, PhoneIncoming, Plus, Users } from 'lucide-react';
import type { TeamSummaryResponse } from '@vagusx/clients';
import { P } from '@vagusx/shared';
import { Route } from '@/routes/_authenticated/setup/teams';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { useTeam, useTeams } from '@/hooks/use-teams';
import { useHasPermission } from '@/hooks/use-permissions';
import { toDraft } from './draft-member';
import { hasStaffedInboundTeam, isInboundCallTeam } from './inbound-default';
import { MemberList } from './MemberList';
import { TeamEditor } from './TeamEditor';

function buildColumns(opts: {
  canEdit: boolean;
  onEdit: (id: string) => void;
}): ColumnDef<TeamSummaryResponse>[] {
  const cols: ColumnDef<TeamSummaryResponse>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.name}</span>
          {isInboundCallTeam(row.original) && (
            <Badge variant="secondary" className="gap-1">
              <PhoneIncoming className="h-3 w-3" />
              Inbound calls
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="line-clamp-1 text-muted-foreground">
          {row.original.description ?? '-'}
        </span>
      ),
    },
    {
      accessorKey: 'memberCount',
      header: 'Members',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.userCount === 1 ? '1 user' : `${row.original.userCount} users`}
        </span>
      ),
    },
  ];

  if (opts.canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[64px]', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          // Hidden until the row is hovered; `focus-visible` keeps it reachable
          // by keyboard. Tailwind gates `group-hover` behind `@media (hover:hover)`,
          // so anything with a touchscreen keeps it permanently visible — including
          // a hybrid laptop, whose *primary* input hovers even when the finger the
          // user actually taps with does not. Without that the button would be
          // invisible but still tappable, swallowing the row tap. `any-pointer`
          // (not `hover`) is the test, because it considers every attached input.
          // No `transition-opacity` here: Button's base `transition-all` already
          // covers opacity, and tailwind-merge would drop it for a narrower class.
          className="h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          aria-label={`Edit ${row.original.name}`}
          onClick={(e) => {
            e.stopPropagation();
            opts.onEdit(row.original.id);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    });
  }

  return cols;
}

export default function TeamsPage(): React.ReactElement {
  const { id: selectedTeamId } = Route.useSearch();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const canEdit = useHasPermission(P.TEAMS_EDIT);

  const { data: teams, isLoading, error } = useTeams();

  // Row click opens view mode; pencil icon opens edit mode directly.
  const openView = useCallback(
    (id: string) => {
      setIsEditing(false);
      navigate({ to: '/setup/teams', search: { id }, replace: true });
    },
    [navigate]
  );

  const openEdit = useCallback(
    (id: string) => {
      setIsEditing(true);
      navigate({ to: '/setup/teams', search: { id }, replace: true });
    },
    [navigate]
  );

  const handleSelect = useCallback((team: TeamSummaryResponse) => openView(team.id), [openView]);

  const handleClose = useCallback(() => {
    setIsCreating(false);
    setIsEditing(false);
    navigate({ to: '/setup/teams', search: {}, replace: true });
  }, [navigate]);

  const columns = useMemo(() => buildColumns({ canEdit, onEdit: openEdit }), [canEdit, openEdit]);

  return (
    <TablePage
      title="Teams"
      description="Group users and agents that can be assigned tasks together"
      columns={columns}
      data={teams ?? []}
      total={teams?.length || undefined}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search teams..."
      onRowClick={handleSelect}
      emptyMessage="No teams yet. Create one to start routing tasks to a group."
      errorMessage="Failed to load teams."
      aboveTable={
        teams && teams.length > 0 && !hasStaffedInboundTeam(teams) ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4 shrink-0" />
            No team with members takes inbound calls, so their follow-ups go to your assignment
            rules and may not appear under anyone&apos;s My tasks. Turn on &quot;Default team for
            inbound calls&quot; on the team that answers the phone.
          </p>
        ) : undefined
      }
      headerAction={
        canEdit ? (
          <Button
            onClick={() => {
              // Switching from view/edit to create: drop the URL id so the
              // view/edit sheet closes cleanly.
              if (selectedTeamId) {
                navigate({ to: '/setup/teams', search: {}, replace: true });
                setIsEditing(false);
              }
              setIsCreating(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            New Team
          </Button>
        ) : undefined
      }
    >
      {/* Create sheet */}
      <Sheet open={isCreating} onOpenChange={(open) => !open && setIsCreating(false)}>
        <SheetContent className="w-[520px] sm:max-w-[520px]">
          <TeamEditor
            onClose={() => setIsCreating(false)}
            onCreated={(id) => {
              // Drop the create flag and open the new team in view mode
              // so the user can confirm what was saved.
              setIsCreating(false);
              openView(id);
            }}
          />
        </SheetContent>
      </Sheet>

      {/* View / Edit sheet — same drawer, different contents based on isEditing */}
      <Sheet open={!!selectedTeamId && !isCreating} onOpenChange={(open) => !open && handleClose()}>
        <SheetContent className="w-[520px] sm:max-w-[520px]">
          {selectedTeamId && isEditing && (
            <TeamEditor
              key={`edit-${selectedTeamId}`}
              teamId={selectedTeamId}
              onClose={handleClose}
            />
          )}
          {selectedTeamId && !isEditing && (
            <TeamDetailSheet
              teamId={selectedTeamId}
              canEdit={canEdit}
              onEdit={() => setIsEditing(true)}
              onClose={handleClose}
            />
          )}
        </SheetContent>
      </Sheet>
    </TablePage>
  );
}

// =============================================================================
// Read-only team detail
// =============================================================================

function TeamDetailSheet({
  teamId,
  canEdit,
  onEdit,
  onClose,
}: {
  teamId: string;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
}): React.ReactElement {
  const { data: team, isLoading, error } = useTeam(teamId);

  if (isLoading) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Loading…</SheetTitle>
        </SheetHeader>
        <div className="flex-1 px-4 py-4 text-sm text-muted-foreground">Loading team…</div>
      </>
    );
  }

  // Team was deleted out-from-under us (e.g. another admin deleted it in
  // another tab) or the fetch failed. Don't strand the user on a
  // perpetual loading state — explain and offer to close.
  if (error || !team) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Team unavailable</SheetTitle>
          <SheetDescription>
            This team may have been deleted. Close the panel and refresh the list.
          </SheetDescription>
        </SheetHeader>
        <SheetBody />
        <SheetActions>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </SheetActions>
      </>
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-muted-foreground" />
          {team.name}
        </SheetTitle>
        <SheetDescription>Team details</SheetDescription>
      </SheetHeader>

      <SheetBody>
        <div className="space-y-2">
          <DetailSection title="General">
            <DetailRow label="Name" value={team.name} />
            {isInboundCallTeam(team) && <DetailRow label="Inbound calls" value="Default team" />}
          </DetailSection>

          {team.description && (
            <DetailSection title="Description">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {team.description}
              </p>
            </DetailSection>
          )}

          <DetailSection
            title={
              <>
                Members
                <span className="ml-1 text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
                  ({team.userCount})
                </span>
              </>
            }
          >
            <MemberList members={team.members.map(toDraft)} emptyMessage="No members yet." />
          </DetailSection>
        </div>
      </SheetBody>

      {canEdit && (
        <SheetActions>
          <SheetCancel />
          <Button onClick={onEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Edit
          </Button>
        </SheetActions>
      )}
    </>
  );
}
